/**
 * @module features/client-device-management/components/AssignDeviceModal
 * @description Responsive Drawer (mobile bottom sheet / desktop slide-over) for
 * assigning a single unassigned hardware device from central inventory to `{Org}`.
 * Includes real-time inventory search, maintenance-unit confirmation warning, and
 * initial custom device name / deployment location seeding.
 */
import * as React from "react";
import {
  AlertCircle,
  Check,
  Cpu,
  Loader2,
  Search,
  TriangleAlert,
} from "lucide-react";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppError } from "@/lib/api-client";
import { AvailableDevice, formatDeviceTypeLabel } from "../types";
import { useAssignDevice, useAvailableDevices } from "../api/assignmentApi";

export interface AssignDeviceModalProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  orgName: string;
  deviceTypes: ReadonlyArray<{ type_id: string; label: string }>;
}

export function AssignDeviceModal({
  open,
  onClose,
  clientId,
  orgName,
  deviceTypes,
}: AssignDeviceModalProps) {
  const [searchInput, setSearchInput] = React.useState("");
  const deferredSearch = React.useDeferredValue(searchInput.trim());
  const [deviceTypeFilter, setDeviceTypeFilter] = React.useState("all");
  const [selectedDevice, setSelectedDevice] =
    React.useState<AvailableDevice | null>(null);
  const [confirmMaintenance, setConfirmMaintenance] = React.useState(false);
  const [customDeviceName, setCustomDeviceName] = React.useState("");
  const [location, setLocation] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [submitError, setSubmitError] = React.useState<AppError | null>(null);

  const availableQuery = useAvailableDevices(
    {
      search: deferredSearch || undefined,
      device_type: deviceTypeFilter,
    },
    open
  );
  const assignMutation = useAssignDevice(clientId);

  const resetLocalState = React.useCallback(() => {
    setSearchInput("");
    setDeviceTypeFilter("all");
    setSelectedDevice(null);
    setConfirmMaintenance(false);
    setCustomDeviceName("");
    setLocation("");
    setNotes("");
    setSubmitError(null);
  }, []);

  const handleClose = (): void => {
    resetLocalState();
    onClose();
  };

  const isMaintenanceSelected =
    selectedDevice?.status?.toUpperCase() === "MAINTENANCE";

  const handleAssignSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!selectedDevice) return;
    if (isMaintenanceSelected && !confirmMaintenance) return;

    setSubmitError(null);
    try {
      await assignMutation.mutateAsync({
        device_id: selectedDevice.id,
        custom_device_name: customDeviceName.trim(),
        location: location.trim(),
        notes: notes.trim(),
      });
      handleClose();
    } catch (rawErr: unknown) {
      setSubmitError(
        AppError.fromUnknown(rawErr, "Failed to assign hardware device.")
      );
    }
  };

  const availableDevices = availableQuery.data ?? [];

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={`Assign Device to ${orgName}`}
      description="Select an unassigned unit from central inventory and optionally pre-fill its deployment details."
    >
      <form onSubmit={handleAssignSubmit} className="space-y-4" noValidate>
        {submitError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger"
          >
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
            <div className="min-w-0">
              <p>{submitError.errorDetails}</p>
              {submitError.requestId && (
                <p className="mt-1 font-mono text-[11px] text-muted-foreground break-all">
                  Request ID: {submitError.requestId}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Inventory Search & Type Filter */}
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5">
          <div className="sm:col-span-7 relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              inputMode="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search MAC, ID, or name..."
              aria-label="Search unassigned devices"
              className="pl-9 h-11 sm:h-10 text-base sm:text-sm bg-card"
            />
          </div>
          <div className="sm:col-span-5">
            <Select
              value={deviceTypeFilter}
              onValueChange={(val) => setDeviceTypeFilter(val)}
            >
              <SelectTrigger
                aria-label="Filter available devices by gateway type"
                className="h-11 sm:h-10 bg-card rounded-xl"
              >
                <SelectValue placeholder="All gateway types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Gateway Types</SelectItem>
                {deviceTypes.map((dt) => (
                  <SelectItem key={dt.type_id} value={dt.type_id}>
                    {dt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Available Unassigned Inventory Picker */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-foreground">
            Available Hardware Units ({availableDevices.length})
          </p>

          {availableQuery.isLoading ? (
            <div className="space-y-2" aria-busy="true">
              <Skeleton className="h-14 w-full rounded-xl" />
              <Skeleton className="h-14 w-full rounded-xl" />
              <Skeleton className="h-14 w-full rounded-xl" />
            </div>
          ) : availableQuery.isError ? (
            <div
              role="alert"
              className="rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger space-y-2"
            >
              <p>{AppError.fromUnknown(availableQuery.error).errorDetails}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void availableQuery.refetch()}
                className="bg-card text-foreground text-xs"
              >
                Retry
              </Button>
            </div>
          ) : availableDevices.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-6 text-center">
              <Cpu
                className="mx-auto h-6 w-6 text-muted-foreground"
                aria-hidden="true"
              />
              <p className="mt-2 text-xs font-medium text-foreground">
                No unassigned devices found
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Register new hardware in Device Management or adjust your search.
              </p>
            </div>
          ) : (
            <div
              role="listbox"
              aria-label="Unassigned hardware devices"
              className="max-h-56 overflow-y-auto rounded-xl border border-border/80 divide-y divide-border/60 bg-muted/15"
            >
              {availableDevices.map((device) => {
                const isSelected = selectedDevice?.id === device.id;
                const isMaint = device.status?.toUpperCase() === "MAINTENANCE";

                return (
                  <button
                    key={device.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      setSelectedDevice(device);
                      setConfirmMaintenance(false);
                    }}
                    className={`w-full text-left px-3.5 py-2.5 flex items-center justify-between gap-3 transition-colors touch:min-h-[48px] ${
                      isSelected
                        ? "bg-primary/10 text-foreground"
                        : "hover:bg-muted/50 text-foreground"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium truncate">
                          {device.device_name}
                        </span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground shrink-0">
                          {formatDeviceTypeLabel(device.device_type, deviceTypes)}
                        </span>
                        {isMaint && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft text-warning border border-warning/20 px-2 py-0.5 text-[10px] font-medium shrink-0">
                            <TriangleAlert
                              className="h-2.5 w-2.5"
                              aria-hidden="true"
                            />
                            Maintenance
                          </span>
                        )}
                      </div>
                      <p className="font-mono text-[11px] text-muted-foreground truncate mt-0.5">
                        {device.registered_device_id}
                        {device.mac_address ? ` · ${device.mac_address}` : ""}
                      </p>
                    </div>
                    {isSelected && (
                      <Check
                        className="h-4 w-4 text-foreground shrink-0"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Maintenance Warning Guard (Spec Acceptance Scenario 1.5) */}
        {isMaintenanceSelected && (
          <div className="rounded-xl border border-warning/30 bg-warning-soft p-3 space-y-2 text-xs">
            <div className="flex items-start gap-2 text-warning font-medium">
              <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
              <p>
                This unit is currently flagged as in Maintenance. Confirm below before
                assigning it to {orgName}.
              </p>
            </div>
            <div className="flex items-center gap-2 pl-6">
              <Checkbox
                id="confirm-maintenance-assign"
                checked={confirmMaintenance}
                onCheckedChange={(checked) => setConfirmMaintenance(checked)}
              />
              <label
                htmlFor="confirm-maintenance-assign"
                className="text-xs text-foreground cursor-pointer select-none"
              >
                I confirm assigning this maintenance unit
              </label>
            </div>
          </div>
        )}

        {/* Initial Client Configuration Seed Fields */}
        <div className="space-y-3 rounded-xl border border-border/80 bg-muted/20 p-3.5">
          <p className="text-xs font-medium text-foreground">
            Initial Client Configuration (Optional)
          </p>

          <div className="space-y-1">
            <label
              htmlFor="assign-custom-name"
              className="text-xs text-muted-foreground block"
            >
              Custom Device Name
            </label>
            <Input
              id="assign-custom-name"
              maxLength={150}
              value={customDeviceName}
              onChange={(e) => setCustomDeviceName(e.target.value)}
              placeholder="e.g. Main Lobby Reception Display"
              className="h-11 sm:h-9 text-base sm:text-sm bg-card"
            />
          </div>

          <div className="space-y-1">
            <label
              htmlFor="assign-location"
              className="text-xs text-muted-foreground block"
            >
              Deployment Location
            </label>
            <Input
              id="assign-location"
              maxLength={255}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Building A, Floor 1 Reception"
              className="h-11 sm:h-9 text-base sm:text-sm bg-card"
            />
          </div>

          <div className="space-y-1">
            <label
              htmlFor="assign-notes"
              className="text-xs text-muted-foreground block"
            >
              Allocation Notes
            </label>
            <Input
              id="assign-notes"
              maxLength={255}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional ticket or rollout reference"
              className="h-11 sm:h-9 text-base sm:text-sm bg-card"
            />
          </div>
        </div>

        <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={assignMutation.isPending}
            className="h-10 sm:h-9 touch:min-h-[44px] px-4 text-xs"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              !selectedDevice ||
              (isMaintenanceSelected && !confirmMaintenance) ||
              assignMutation.isPending
            }
            className="h-10 sm:h-9 touch:min-h-[44px] px-4 text-xs min-w-[140px]"
          >
            {assignMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                <span>Assigning...</span>
              </>
            ) : (
              <span>Assign to {orgName}</span>
            )}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

